import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { loggedInApiCall, resolveRequestLocale } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

import { IngredientReferenceUnified, IngredientReferenceUnifiedSchema, SURE_API_DEGRADED_HEADER } from "./types";

export const getSomeIngredientReferencesUnifiedKey = "someIngredientReferencesUnified" as const;

const BATCH_SIZE = 200;
const MAX_HYDRA_PAGES_PER_BATCH = 1_000;
const INGREDIENT_REFERENCES_ENDPOINT = "/api/ingredient-references-unified";

const IngredientReferencePageSchema = z.object({
    "hydra:member": IngredientReferenceUnifiedSchema.array(),
    "hydra:totalItems": z.number().int().nonnegative().optional(),
    "hydra:view": z
        .object({
            "hydra:next": z.string().min(1).optional(),
        })
        .optional(),
});

const chunk = <T>(items: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let index = 0; index < items.length; index += size) {
        chunks.push(items.slice(index, index + size));
    }

    return chunks;
};

const getNextLinkIds = (searchParams: URLSearchParams): string[] => {
    const ids: string[] = [];
    searchParams.forEach((value, key) => {
        if (key === "id" || key === "id[]" || /^id\[\d+\]$/.test(key)) {
            ids.push(value);
        }
    });

    return ids;
};

const isIdQueryParameter = (key: string): boolean => key === "id" || key === "id[]" || /^id\[\d+\]$/.test(key);

const hasOnlyBatchQueryParameters = (searchParams: URLSearchParams): boolean => {
    let valid = true;
    searchParams.forEach((_value, key) => {
        if (!isIdQueryParameter(key) && key !== "itemsPerPage" && key !== "organization_id" && key !== "page") {
            valid = false;
        }
    });

    return valid;
};

const hasSinglePositiveIntegerValue = (searchParams: URLSearchParams, key: string, expectedValue?: number): boolean => {
    const values = searchParams.getAll(key);
    if (values.length !== 1 || !/^[1-9]\d*$/.test(values[0] ?? "")) {
        return false;
    }

    return expectedValue === undefined || values[0] === String(expectedValue);
};

const assertSureBatchIsComplete = (ids: string[], isDegraded: boolean): void => {
    if (isDegraded && ids.some((id) => id.startsWith("sure-"))) {
        throw new Error("Sure ingredient reference batch is degraded");
    }
};

const normalizeScopedNextLink = (
    nextLink: string,
    requestedIds: string[],
    organizationId: number,
    expectedPage: number,
): string => {
    const fallbackBaseUrl = "https://hydra.local";
    let endpointUrl: URL;
    let nextUrl: URL;

    try {
        const apiBaseUrl = new URL(loggedInApiCall.defaults.baseURL ?? "/", fallbackBaseUrl);
        endpointUrl = new URL(INGREDIENT_REFERENCES_ENDPOINT, apiBaseUrl);
        nextUrl = new URL(nextLink, endpointUrl);
    } catch {
        throw new Error(`Invalid hydra:next received for ${INGREDIENT_REFERENCES_ENDPOINT}`);
    }

    if (nextUrl.origin !== endpointUrl.origin || nextUrl.pathname !== endpointUrl.pathname) {
        throw new Error(`Hydra next link changes collection scope for ${INGREDIENT_REFERENCES_ENDPOINT}`);
    }

    if (
        !hasOnlyBatchQueryParameters(nextUrl.searchParams) ||
        !hasSinglePositiveIntegerValue(nextUrl.searchParams, "itemsPerPage", requestedIds.length) ||
        !hasSinglePositiveIntegerValue(nextUrl.searchParams, "page", expectedPage)
    ) {
        throw new Error(`Hydra next link changes query scope for ${INGREDIENT_REFERENCES_ENDPOINT}`);
    }

    const expectedIds = [...requestedIds].sort();
    const nextIds = getNextLinkIds(nextUrl.searchParams).sort();
    if (nextIds.length !== expectedIds.length || nextIds.some((id, index) => id !== expectedIds[index])) {
        throw new Error(`Hydra next link drops requested ids for ${INGREDIENT_REFERENCES_ENDPOINT}`);
    }

    const nextOrganizationIds = nextUrl.searchParams.getAll("organization_id");
    if (nextOrganizationIds.length !== 1 || nextOrganizationIds[0] !== String(organizationId)) {
        throw new Error(`Hydra next link changes organization scope for ${INGREDIENT_REFERENCES_ENDPOINT}`);
    }

    return nextUrl.pathname + nextUrl.search;
};

const fetchReferenceBatch = async (ids: string[], organizationId: number): Promise<IngredientReferenceUnified[]> => {
    const requestedIds = new Set(ids);
    const references = new Map<string, IngredientReferenceUnified>();
    let expectedTotal: number | undefined;
    let pagesFetched = 0;
    let fallbackPage = 1;
    let nextLink: string | undefined;

    const addPage = (responseData: unknown) => {
        const page = zodParseWithLog(
            responseData,
            IngredientReferencePageSchema,
            getSomeIngredientReferencesUnifiedKey,
        );
        if (
            expectedTotal !== undefined &&
            page["hydra:totalItems"] !== undefined &&
            page["hydra:totalItems"] !== expectedTotal
        ) {
            throw new Error(`Inconsistent hydra:totalItems received for ${INGREDIENT_REFERENCES_ENDPOINT}`);
        }
        expectedTotal ??= page["hydra:totalItems"];

        for (const reference of page["hydra:member"]) {
            if (!requestedIds.has(reference.compositeId)) {
                throw new Error(`Hydra page returned an unrequested reference ${reference.compositeId}`);
            }
            if (!references.has(reference.compositeId)) {
                references.set(reference.compositeId, reference);
            }
        }

        if (expectedTotal !== undefined && references.size > expectedTotal) {
            throw new Error(`Hydra pagination exceeded its total for ${INGREDIENT_REFERENCES_ENDPOINT}`);
        }

        nextLink = page["hydra:view"]?.["hydra:next"];
    };

    const firstResponse = await loggedInApiCall.get(INGREDIENT_REFERENCES_ENDPOINT, {
        params: {
            id: ids,
            itemsPerPage: ids.length,
            organization_id: organizationId,
            page: fallbackPage,
        },
    });
    pagesFetched += 1;
    assertSureBatchIsComplete(ids, firstResponse.headers?.[SURE_API_DEGRADED_HEADER] === "1");
    addPage(firstResponse.data);

    const visitedNextLinks = new Set<string>();
    while (nextLink !== undefined) {
        const normalizedNextLink = normalizeScopedNextLink(nextLink, ids, organizationId, pagesFetched + 1);
        if (visitedNextLinks.has(normalizedNextLink)) {
            throw new Error(`Cyclic Hydra pagination link received for ${INGREDIENT_REFERENCES_ENDPOINT}`);
        }
        if (pagesFetched >= MAX_HYDRA_PAGES_PER_BATCH) {
            throw new Error(`Hydra pagination exceeded safe page limit for ${INGREDIENT_REFERENCES_ENDPOINT}`);
        }
        visitedNextLinks.add(normalizedNextLink);

        const referencesBeforePage = references.size;
        const response = await loggedInApiCall.get(normalizedNextLink);
        pagesFetched += 1;
        assertSureBatchIsComplete(ids, response.headers?.[SURE_API_DEGRADED_HEADER] === "1");
        addPage(response.data);

        if (references.size === referencesBeforePage) {
            throw new Error(`Hydra pagination made no progress for ${INGREDIENT_REFERENCES_ENDPOINT}`);
        }
    }

    fallbackPage = pagesFetched + 1;
    while (expectedTotal !== undefined && references.size < expectedTotal) {
        if (pagesFetched >= MAX_HYDRA_PAGES_PER_BATCH) {
            throw new Error(`Hydra pagination exceeded safe page limit for ${INGREDIENT_REFERENCES_ENDPOINT}`);
        }

        const referencesBeforePage = references.size;
        const response = await loggedInApiCall.get(INGREDIENT_REFERENCES_ENDPOINT, {
            params: {
                id: ids,
                itemsPerPage: ids.length,
                organization_id: organizationId,
                page: fallbackPage,
            },
        });
        pagesFetched += 1;
        fallbackPage += 1;
        assertSureBatchIsComplete(ids, response.headers?.[SURE_API_DEGRADED_HEADER] === "1");
        addPage(response.data);

        if (references.size === referencesBeforePage) {
            throw new Error(`Hydra pagination made no progress for ${INGREDIENT_REFERENCES_ENDPOINT}`);
        }
    }

    if (expectedTotal !== undefined && references.size !== expectedTotal) {
        throw new Error(`Hydra pagination cardinality mismatch for ${INGREDIENT_REFERENCES_ENDPOINT}`);
    }

    return [...references.values()];
};

/**
 * Batch fetch ingredient references by composite id (`{source}-{id}`) — used by the table
 * column to resolve every displayed row through one cached query.
 *
 * Returns an empty/undefined result when the input array is empty or undefined to avoid
 * issuing a request that would either 400 or download the full catalog.
 */
export const useGetSomeIngredientReferencesUnified = (compositeIds: string[] | undefined) => {
    const { selectedCompany } = useFoodpilotContext();
    const { i18n } = useTranslation();
    const locale = resolveRequestLocale(undefined, i18n.resolvedLanguage);
    const organizationId = selectedCompany?.id;
    const uniqueCompositeIds = compositeIds ? Array.from(new Set(compositeIds)) : undefined;
    const isEnabled = organizationId !== undefined && uniqueCompositeIds !== undefined && uniqueCompositeIds.length > 0;

    // Stable cache key independent of input ordering. Multiple table renders should hit the
    // same query entry as long as the underlying set of ids is the same.
    const sortedKey = uniqueCompositeIds ? [...uniqueCompositeIds].sort() : [];

    const query = useQuery({
        enabled: isEnabled,
        queryKey: [getSomeIngredientReferencesUnifiedKey, organizationId ?? null, locale, sortedKey],
        keepPreviousData: false,
        queryFn: async () => {
            if (
                !isEnabled ||
                organizationId === undefined ||
                uniqueCompositeIds === undefined ||
                uniqueCompositeIds.length === 0
            ) {
                throw new Error(
                    "Cannot load ingredient references without their tenant scope and at least one composite id",
                );
            }

            const batches = chunk(uniqueCompositeIds, BATCH_SIZE);

            return await Promise.all(batches.map((ids) => fetchReferenceBatch(ids, organizationId)));
        },
        select: (batches) => {
            const items = batches.flat();
            const data = zodParseWithLog(
                items,
                IngredientReferenceUnifiedSchema.array(),
                getSomeIngredientReferencesUnifiedKey,
            );
            return [...new Map(data.map((reference) => [reference.compositeId, reference])).values()];
        },
    });

    return {
        ...query,
        data: isEnabled ? query.data : undefined,
        ingredientReferences: isEnabled ? (query.data as IngredientReferenceUnified[] | undefined) : [],
        isLoading: isEnabled && query.isLoading,
    };
};
